import * as Sentry from "@sentry/react";
import { useEffect } from "react";
import {
  createRoutesFromChildren,
  matchRoutes,
  useLocation,
  useNavigationType,
} from "react-router";

// Runtime release identifier injected by the platform server when serving index.html
// (see apps/platform/src/sentryRelease.ts). Build-time injection doesn't work on
// Railway because RAILWAY_GIT_COMMIT_SHA is runtime-only; the platform stamps the
// release in as it serves the SPA HTML. Uses a meta tag to comply with CSP
// `script-src 'self'` (no inline scripts).

/**
 * Initialize Sentry for web-admin. Early-returns when no DSN is configured
 * (e.g., local dev) so Sentry stays silent unless explicitly enabled.
 */
export function initSentry(): void {
  const dsn = import.meta.env.VITE_ADMIN_SENTRY_DSN;
  if (!dsn) return;

  const injected =
    typeof document !== "undefined"
      ? document.querySelector<HTMLMetaElement>('meta[name="sentry-release"]')?.content
      : undefined;
  const release = typeof injected === "string" && injected ? injected : undefined;

  Sentry.init({
    dsn,
    environment: import.meta.env.VITE_ADMIN_SENTRY_ENV ?? import.meta.env.MODE,
    release,
    integrations: [
      Sentry.reactRouterV7BrowserTracingIntegration({
        useEffect,
        useLocation,
        useNavigationType,
        createRoutesFromChildren,
        matchRoutes,
      }),
    ],
    // 100% sampling: web-admin is internal-only with low traffic volume.
    tracesSampleRate: 1.0,
    // v11 removed `sendDefaultPii`; every `dataCollection` category defaults to
    // ON, so each is declared off explicitly. See history: SIM-2470, SIM-3355.
    dataCollection: {
      userInfo: false,
      cookies: false,
      httpHeaders: false,
      httpBodies: [],
      urlQueryParams: false,
      graphQL: { document: false, variables: false },
      genAI: { inputs: false, outputs: false },
      mcp: { inputs: false, outputs: false },
      databaseQueryData: false,
      queues: false,
      stackFrameVariables: false,
    },
    ignoreErrors: [
      "ResizeObserver loop limit exceeded",
      "ResizeObserver loop completed with undelivered notifications",
    ],
    denyUrls: [/^chrome-extension:\/\//, /^moz-extension:\/\//],
    beforeSend(event, hint) {
      // Drop session-expiry fetch errors from the /api/reports/runs polling loop.
      // App.tsx treats 401/403 as "redirect to login" — not a real error. Anchored so a
      // server detail that merely mentions "HTTP 401" inside an ApiError isn't dropped.
      const err = hint.originalException;
      if (err instanceof Error && /^HTTP 40[13]\b/.test(err.message)) return null;
      return event;
    },
  });
}
